import Vue from 'vue';
import VueI18n from 'vue-i18n';
import Cookies from 'js-cookie';
import moment from 'moment';
import 'moment/locale/tr';
import 'moment/locale/ru';

Vue.use(VueI18n);

export interface LocaleOption {
  code: string;
  name: string;
  flag: string;
  culture: string;
  momentLocale: string;
}

export const LOCALE_COOKIE_NAME = 'app.locale';
export const LOCALE_COOKIE_EXPIRE_DAYS = 365;
export const FALLBACK_LOCALE = 'tr';

export const LOCALES: LocaleOption[] = [
  { code: 'tr', name: 'Türkçe', flag: 'tr', culture: 'tr-TR', momentLocale: 'tr' },
  { code: 'en', name: 'English', flag: 'gb', culture: 'en-GB', momentLocale: 'en' },
  { code: 'ru', name: 'Русский', flag: 'ru', culture: 'ru-RU', momentLocale: 'ru' },
];

function isSupported(code: string): boolean {
  return LOCALES.some((l) => l.code === code);
}

export function getLocaleOption(code: string): LocaleOption {
  return LOCALES.find((l) => l.code === code) || LOCALES[0];
}

function loadMessages(): VueI18n.LocaleMessages {
  const context = require.context('./locales', true, /\.json$/);
  const messages: VueI18n.LocaleMessages = {};
  context.keys().forEach((key) => {
    const matched = key.match(/^\.\/([a-z]{2})\/(.+)\.json$/);
    if (!matched) return;
    const locale = matched[1];
    const namespace = matched[2];
    if (!messages[locale]) messages[locale] = {};
    const loaded = context(key);
    messages[locale][namespace] = loaded && loaded.default ? loaded.default : loaded;
  });
  return messages;
}

/**
 * Kullanıcının daha önce yazılmış cookie tercihi her zaman kazanır; yoksa
 * tarayıcı diline, o da desteklenmiyorsa Türkçe'ye düşer.
 */
export function resolveInitialLocale(): string {
  const fromCookie = Cookies.get(LOCALE_COOKIE_NAME);
  if (fromCookie && isSupported(fromCookie)) return fromCookie;

  const fromBrowser = (navigator.language || '').slice(0, 2).toLowerCase();
  if (isSupported(fromBrowser)) return fromBrowser;

  return FALLBACK_LOCALE;
}

const initialLocale = resolveInitialLocale();

const i18n = new VueI18n({
  locale: initialLocale,
  fallbackLocale: FALLBACK_LOCALE,
  silentFallbackWarn: true,
  messages: loadMessages(),
});

/**
 * vue-class-component, sınıf alanı başlatıcılarını `data()` içinde çalıştırırken
 * gerçek bileşene yalnızca `_` ile başlamayan özellikleri proxy'ler. Bu nedenle
 * `this._i18n` o anda tanımsızdır ve `this.$t(...)` alan başlatıcılarında hata
 * verir. Aşağıdaki sarmalayıcı böyle durumlarda kök i18n örneğine düşer.
 */
const fallbackMethods: Array<[string, keyof VueI18n]> = [
  ['$t', 't'],
  ['$tc', 'tc'],
  ['$te', 'te'],
  ['$d', 'd'],
  ['$n', 'n'],
];

fallbackMethods.forEach(([vueMethod, i18nMethod]) => {
  const original = (Vue.prototype as any)[vueMethod];
  (Vue.prototype as any)[vueMethod] = function(this: any, ...args: any[]) {
    if (this && this._i18n) return original.apply(this, args);
    return (i18n[i18nMethod] as any).apply(i18n, args);
  };
});

function persistLocale(code: string) {
  const option = getLocaleOption(code);
  Cookies.set(LOCALE_COOKIE_NAME, code, {
    expires: LOCALE_COOKIE_EXPIRE_DAYS,
    path: '/',
  });
  // ABP sunucu tarafı mesajlarının da aynı dilde dönmesi için
  Cookies.set('Abp.Localization.CultureName', option.culture, {
    expires: LOCALE_COOKIE_EXPIRE_DAYS,
    path: '/',
  });
}

export function applyLocale(code: string) {
  const option = getLocaleOption(code);
  i18n.locale = option.code;
  moment.locale(option.momentLocale);
  document.documentElement.setAttribute('lang', option.code);
  persistLocale(option.code);
}

export function setLocale(code: string) {
  if (!isSupported(code) || code === i18n.locale) return;
  applyLocale(code);
  // Sayfalarda script içinde bir kez hesaplanan başlık/kolon metinlerinin de
  // yeni dile geçmesi için tam yenileme yapılıyor.
  location.reload();
}

export function getLocale(): string {
  return i18n.locale;
}

applyLocale(initialLocale);

export default i18n;
